import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import type { EmployeeSecretResult, EmployeeSummary, Role } from '@blesk/contracts';
import { apiPost } from '../../shared/api';
import { usePagedList } from '../../shared/lists';
import { EMPLOYEE_STATUS_LABEL, ROLE_LABEL } from '../../shared/labels';
import { formatInProjectTz } from '../../shared/format';
import { useHasPermission, useSession } from '../../shared/session';
import { useUrlFilter } from '../../shared/filters';
import { PermissionGate } from '../shell/PermissionGate';
import { EmployeeCreateDialog, PasswordDialog } from '../shell/EmployeeDialogs';
import { Dialog, EmptyRow, ErrorNote, Pager, RowMenu, orDash, useConfirm } from '../shell/ui';

/** §3, §13, §15.3 ТЗ. Раздел «Пользователи и роли». */
export function EmployeesPage() {
  return (
    <PermissionGate permission="employees.read">
      <EmployeesInner />
    </PermissionGate>
  );
}

/**
 * Действие над учётной записью, ожидающее подтверждения.
 *
 * Смена роли и блокировка одинаково меняют доступ (§3), поэтому спрашиваются
 * одинаково: инлайн-селект, применяющийся с первого промаха мышью, тихо выдал
 * бы права системного администратора.
 */
function EmployeesInner() {
  const session = useSession();
  const canWrite = useHasPermission('employees.write');
  const queryClient = useQueryClient();

  const [role, setRole] = useUrlFilter('role');
  const [status, setStatus] = useUrlFilter('status');
  const [createOpen, setCreateOpen] = useState(false);
  const [resetFor, setResetFor] = useState<EmployeeSummary | null>(null);
  const [issued, setIssued] = useState<{ employee: string; password: string } | null>(null);
  const confirmation = useConfirm();

  const list = usePagedList<EmployeeSummary>('employees', '/api/employees', { role, status });
  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['employees'] });

  const changeRole = useMutation({
    mutationFn: (input: { id: string; role: Role }) =>
      apiPost(`/api/employees/${input.id}/role`, { role: input.role }),
    onSuccess: invalidate,
  });
  const block = useMutation({
    mutationFn: (id: string) => apiPost(`/api/employees/${id}/block`),
    onSuccess: invalidate,
  });
  const unblock = useMutation({
    mutationFn: (id: string) => apiPost(`/api/employees/${id}/unblock`),
    onSuccess: invalidate,
  });

  return (
    <>
      <h1>Пользователи и роли</h1>
      <p className="muted">
        Сотрудники не удаляются, а блокируются; блокировка обрывает действующие сессии.
      </p>

      <div className="toolbar">
        <label>
          Роль
          <select value={role} onChange={(event) => setRole(event.target.value)}>
            <option value="">Все</option>
            {Object.entries(ROLE_LABEL).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </label>
        <label>
          Статус
          <select value={status} onChange={(event) => setStatus(event.target.value)}>
            <option value="">Все</option>
            {Object.entries(EMPLOYEE_STATUS_LABEL).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </label>
        <span className="toolbar__spacer" />
        {canWrite && (
          <button type="button" className="btn-primary" onClick={() => setCreateOpen(true)}>
            Добавить сотрудника
          </button>
        )}
      </div>

      <ErrorNote error={list.error ?? changeRole.error ?? block.error ?? unblock.error} />

      <div className="table-wrap">
        <table className="table">
          <thead>
            <tr>
              {/* Почта — вторая строка при имени: отдельной колонкой она стоила
                  150 px, и «Последний вход» уезжал за прокрутку (замер 18.08.2026) */}
              <th>Сотрудник</th>
              <th>Роль</th>
              <th>Статус</th>
              <th>Объектов</th>
              <th>Пользователь B24</th>
              <th>Последний вход</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {list.items.length === 0 && <EmptyRow colSpan={7} loading={list.isPending} />}
            {list.items.map((employee) => {
              const self = employee.id === session.employee?.id;
              return (
                <tr key={employee.id}>
                  <td>
                    {employee.fullName}
                    {self && <span className="badge badge--tag">это вы</span>}
                    <div className="cell-note">{employee.email}</div>
                  </td>
                  <td>
                    {canWrite && !self ? (
                      <select
                        aria-label={`Роль · ${employee.fullName}`}
                        // Значение остаётся серверным до подтверждения: выбор в списке
                        // только задаёт вопрос, сам он права не меняет.
                        value={employee.role}
                        onChange={(event) => {
                          const nextRole = event.target.value as Role;
                          confirmation.ask({
                            title: 'Смена роли',
                            question: `Сменить роль «${employee.fullName}»: ${ROLE_LABEL[employee.role]} → ${ROLE_LABEL[nextRole]}?`,
                            warning:
                              'Права изменятся сразу, на действующих сессиях сотрудника тоже.',
                            verb: 'Сменить роль',
                            onConfirm: () => changeRole.mutate({ id: employee.id, role: nextRole }),
                          });
                        }}
                      >
                        {Object.entries(ROLE_LABEL).map(([value, label]) => (
                          <option key={value} value={value}>
                            {label}
                          </option>
                        ))}
                      </select>
                    ) : (
                      ROLE_LABEL[employee.role]
                    )}
                  </td>
                  <td>
                    {EMPLOYEE_STATUS_LABEL[employee.status]}
                    {/* У входящих через портал пароля нет вовсе — им нечего менять,
                        и напоминание читалось бы как ошибка (QA 22.08.2026, прогон 3) */}
                    {employee.mustChangePassword && employee.hasPassword && (
                      <span className="badge badge--state">ждёт смены пароля</span>
                    )}
                  </td>
                  <td>
                    <ManagedProjectsCell employee={employee} />
                  </td>
                  <td>{orDash(employee.bitrixUserId)}</td>
                  <td>{formatInProjectTz(employee.lastLoginAt, session.defaultTimezone)}</td>
                  <td className="row-actions">
                    {canWrite && !self && (
                      <RowMenu
                        actions={[
                          { label: 'Сбросить пароль', onSelect: () => setResetFor(employee) },
                          employee.status === 'BLOCKED'
                            ? {
                                label: 'Разблокировать',
                                onSelect: () => unblock.mutate(employee.id),
                              }
                            : {
                                label: 'Заблокировать',
                                danger: true,
                                onSelect: () =>
                                  confirmation.ask({
                                    title: 'Блокировка сотрудника',
                                    question: `Заблокировать «${employee.fullName}»? Действующие сессии будут прерваны.`,
                                    // Объекты остаются без ответственного: замену назначает
                                    // человек, а не система (§6.1)
                                    warning:
                                      employee.managedProjectCount > 0
                                        ? `Он остаётся менеджером ${managedCountGenitive(employee.managedProjectCount)} — назначьте замену.`
                                        : undefined,
                                    verb: 'Заблокировать',
                                    danger: true,
                                    onConfirm: () => block.mutate(employee.id),
                                  }),
                              },
                        ]}
                      />
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <Pager page={list.page} perPage={list.perPage} total={list.total} onPage={list.setPage} />

      {confirmation.dialog}
      {createOpen && (
        <EmployeeCreateDialog
          onClose={() => setCreateOpen(false)}
          onSaved={invalidate}
          onPassword={setIssued}
        />
      )}
      {resetFor && (
        <ResetDialog
          employee={resetFor}
          onClose={() => setResetFor(null)}
          onSaved={invalidate}
          onPassword={setIssued}
        />
      )}
      {issued && (
        <PasswordDialog
          employee={issued.employee}
          password={issued.password}
          onClose={() => setIssued(null)}
        />
      )}
    </>
  );
}

/**
 * §13: число объектов у менеджера — вход в их список, а не справка.
 * У сотрудника без объектов ссылки нет: она вела бы в заведомо пустую таблицу.
 */
function ManagedProjectsCell({ employee }: { employee: EmployeeSummary }) {
  if (employee.managedProjectCount === 0) return <>0</>;
  return (
    <Link to="/projects" search={{ managerId: employee.id }}>
      {employee.managedProjectCount}
    </Link>
  );
}

function ResetDialog({
  employee,
  onClose,
  onSaved,
  onPassword,
}: {
  employee: EmployeeSummary;
  onClose: () => void;
  onSaved: () => void;
  onPassword: (issued: { employee: string; password: string }) => void;
}) {
  const save = useMutation({
    mutationFn: () =>
      apiPost<EmployeeSecretResult>(`/api/employees/${employee.id}/reset-password`, {}),
    onSuccess: (result) => {
      onSaved();
      onClose();
      if (result.password) {
        onPassword({ employee: result.employee.fullName, password: result.password });
      }
    },
  });

  return (
    <Dialog title={`Сброс пароля · ${employee.fullName}`} onClose={onClose}>
      <form
        className="form"
        onSubmit={(event) => {
          event.preventDefault();
          save.mutate();
        }}
      >
        <p className="muted">
          Действующие сессии сотрудника будут прерваны, при следующем входе он сменит пароль.
          Новый временный пароль сгенерируется и покажется один раз.
        </p>

        <ErrorNote error={save.error} />
        <div className="form__actions">
          <button type="button" onClick={onClose}>
            Отмена
          </button>
          <button type="submit" className="btn-primary" disabled={save.isPending}>
            Сбросить
          </button>
        </div>
      </form>
    </Dialog>
  );
}

/**
 * «Менеджером 1 объекта», «2 объектов», «21 объекта»: родительный падеж
 * после числа. «1 объектов» читалось как машинный текст (QA 22.08.2026).
 */
function managedCountGenitive(count: number): string {
  const noun = count % 10 === 1 && count % 100 !== 11 ? 'объекта' : 'объектов';
  return `${count} ${noun}`;
}
